import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ComponentInOutAnimation } from '@fullyops/shared/util/component-base-animation';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CompaniesService } from '@fullyops/domain/companies/service';

@Component({
  selector: 'crm-contacts-filters',
  templateUrl: './contacts-filters.component.html',
  styleUrls: ['./contacts-filters.component.scss'],
  animations: [ComponentInOutAnimation.AnimTrigger],
  standalone: false,
})
export class ContactsFiltersComponent implements OnInit {
  constructor(private companiesService: CompaniesService) {}

  @Input() filters$: BehaviorSubject<{}> = new BehaviorSubject({});
  @Output() filtersChangeEvent = new EventEmitter<{}>();

  filtersForm: UntypedFormGroup;
  filtersFormFields = [];
  currentFilters$ = new BehaviorSubject({});

  clients$ = new BehaviorSubject<[]>([]);
  assignees$ = new BehaviorSubject<any[]>([]);
  companies$ = new BehaviorSubject<{ id: string; name: string }[]>([]);

  ngOnInit() {
    this.filtersForm = new UntypedFormGroup({
      name: new UntypedFormControl('', []),
      address: new UntypedFormControl('', []),
      email: new UntypedFormControl('', []),
      mobileNumber: new UntypedFormControl(null, []),
      notes: new UntypedFormControl(null, []),
      companyId: new UntypedFormControl(null, []),
    });

    this.companiesService.refreshMini();
    this.companiesService.companies$.subscribe((companies) => {
      this.companies$.next(companies.map((c) => ({ id: c.id, name: c.name })));
    });

    this.filters$.subscribe((filters) => {
      Object.entries(filters).forEach(([key, value]) => {
        if (!this.filtersForm.get(key)) return;

        if (key === 'companyId' && typeof value === 'string') {
          const company = this.companies$.value.find((c) => c.id === value);
          this.filtersForm.get(key).patchValue(company || null);
          return;
        }

        this.filtersForm.get(key).patchValue(value);
      });

      this.updateChips();
    });

    this.filtersFormFields = [
      { name: 'name', type: 'text', label: 'name', size: 12 },
      { name: 'address', type: 'text', label: 'address', size: 12 },
      { name: 'email', type: 'text', label: 'email', size: 12 },
      { name: 'mobileNumber', type: 'text', label: 'mobileNumber', size: 12 },
      { name: 'notes', type: 'text', label: 'notes', size: 12 },
      {
        name: 'companyId',
        type: 'autocomplete',
        label: 'company',
        size: 12,
        items$: this.companies$,
        translate: false,
      },
    ];
  }

  updateChips() {
    const filters = this.filtersForm.value;
    const currentFilters = {};

    Object.keys(filters).forEach((key) => {
      if (
        filters[key] === null ||
        filters[key] === undefined ||
        filters[key].length === 0
      ) {
        delete filters[key];
      } else {
        if (key === 'companyId') {
          const value = filters[key];
          if (typeof value === 'object' && value?.name) {
            currentFilters['company'] = value.name;
          }
          return;
        }
        currentFilters[key] = (filters[key] as string).toLowerCase();
      }
    });

    this.currentFilters$.next(currentFilters);
  }

  getFilterFormValueFormatted() {
    return Object.entries(this.filtersForm.value).reduce(
      (acc, [key, value]) => {
        if (value === undefined || value === null || value === '') return acc;

        if (key === 'companyId') {
          if (typeof value === 'object' && (value as any)?.id) {
            acc['companyId'] = (value as any).id;
          }
          return acc;
        }

        return {
          ...acc,
          [key]: value,
        };
      },
      {},
    );
  }

  applyFilters() {
    this.filtersChangeEvent.emit(this.getFilterFormValueFormatted());
  }

  removeFilter(filter: string) {
    const controlName = filter === 'company' ? 'companyId' : filter;
    this.filtersForm.get(controlName).patchValue(null);
    this.applyFilters();
  }
}
